import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTabNavigationStore } from '../../store/tabNavigationStore'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { PageHeader } from '../../components/ui/PageHeader'
import { paymentSimpleApi } from '../../api/paymentSimple'
import { kopecksToRubles } from '../../types/payment_simple'

export function PaymentSettingsScreen() {
  const { getCurrentScreen, popScreen, pushScreen } = useTabNavigationStore()
  const currentScreen = getCurrentScreen()
  const groupId = currentScreen.params?.groupId as string
  const groupName = (currentScreen.params?.groupName as string) || 'Группа'
  const queryClient = useQueryClient()

  const [isEditing, setIsEditing] = useState(false)
  const [basePrice, setBasePrice] = useState('')
  const [billingDay, setBillingDay] = useState('1')
  const [description, setDescription] = useState('')

  const { data: config, isLoading, status } = useQuery({
    queryKey: ['group-payment-config', groupId],
    queryFn: async () => {
      console.log('🔵 [PaymentSettings] Fetching config for group:', groupId);
      try {
        const result = await paymentSimpleApi.getGroupConfig(groupId);
        console.log('✅ [PaymentSettings] Success:', result);
        return result;
      } catch (error: any) {
        console.log('❌ [PaymentSettings] Error:', error?.response?.status, error?.message);
        throw error;
      }
    },
    enabled: !!groupId,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  })

  console.log('🎯 [PaymentSettings] Render:', {
    groupId,
    status,
    hasConfig: !!config,
    isEditing,
  })

  useEffect(() => {
    if (config && status === 'success') {
      setBasePrice(kopecksToRubles(config.base_price).toString())
      setBillingDay(config.billing_day.toString())
      setDescription(config.description || '')
      setIsEditing(false)
    } else if (status === 'error') {
      // Сбрасываем форму когда настройки удалены (404)
      setBasePrice('')
      setBillingDay('1')
      setDescription('')
      setIsEditing(true)
    }
  }, [config, status])

  const createMutation = useMutation({
    mutationFn: (data: { base_price: number; billing_day: number; description: string }) =>
      paymentSimpleApi.createGroupConfig(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-payment-config', groupId] })
      setIsEditing(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: { base_price: number; billing_day: number; description: string }) =>
      paymentSimpleApi.updateGroupConfig(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-payment-config', groupId] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => paymentSimpleApi.deleteGroupConfig(groupId),
    onSuccess: async () => {
      console.log('🗑️ [PaymentSettings] Delete success, clearing cache...')

      // Очищаем кеш синхронно
      queryClient.setQueryData(['group-payment-config', groupId], undefined)
      queryClient.setQueryData(['group-balances', groupId], undefined)
      console.log('🗑️ [PaymentSettings] Cache cleared with setQueryData')

      // Инвалидируем запросы для перезагрузки
      await queryClient.invalidateQueries({ queryKey: ['group-payment-config', groupId] })
      await queryClient.invalidateQueries({ queryKey: ['group-balances', groupId] })
      console.log('🗑️ [PaymentSettings] Queries invalidated')

      // Проверяем что в кеше
      const cachedConfig = queryClient.getQueryData(['group-payment-config', groupId])
      console.log('🗑️ [PaymentSettings] Cached config after invalidation:', cachedConfig)

      // Теперь возвращаемся на предыдущий экран
      console.log('🗑️ [PaymentSettings] Popping screen...')
      popScreen()
    },
  })

  const handleSave = () => {
    if (!basePrice || parseFloat(basePrice) < 0) {
      alert('Введите корректную цену')
      return
    }
    const day = parseInt(billingDay)
    if (day < 1 || day > 31) {
      alert('День списания должен быть от 1 до 31')
      return
    }

    const data = {
      base_price: parseFloat(basePrice),
      billing_day: day,
      description,
    }

    if (config && status === 'success') {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const handleCancel = () => {
    if (config && status === 'success') {
      setBasePrice(kopecksToRubles(config.base_price).toString())
      setBillingDay(config.billing_day.toString())
      setDescription(config.description || '')
    } else {
      setBasePrice('')
      setBillingDay('1')
      setDescription('')
    }
    setIsEditing(false)
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid var(--separator)',
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: '17px',
    outline: 'none',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    color: 'var(--text-hint)',
    marginBottom: '4px',
  }

  if (!groupId) {
    return (
      <div className="p-4" style={{ color: 'var(--danger)' }}>
        Группа не найдена
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: 'var(--text-hint)' }}>Загрузка...</p>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader title="Настройки оплаты" onBackClick={popScreen} />

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[17px] font-semibold" style={{ color: 'var(--text-primary)' }}>
            {groupName}
          </h3>
          {config && status === 'success' && !isEditing && (
            <Button variant="secondary" onClick={() => setIsEditing(true)}>
              Изменить
            </Button>
          )}
        </div>

        {isEditing || status !== 'success' ? (
          <div className="space-y-3">
            <div>
              <label style={labelStyle}>Базовая цена (₽/месяц)</label>
              <input
                type="number"
                value={basePrice}
                onChange={(e) => setBasePrice(e.target.value)}
                placeholder="5000"
                style={inputStyle}
                min="0"
                step="100"
              />
            </div>

            <div>
              <label style={labelStyle}>День списания (1-31)</label>
              <input
                type="number"
                value={billingDay}
                onChange={(e) => setBillingDay(e.target.value)}
                style={inputStyle}
                min="1"
                max="31"
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-hint)' }}>
                День месяца, когда будет автоматически списываться оплата
              </p>
            </div>

            <div>
              <label style={labelStyle}>Описание (необязательно)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Комментарий о тарифе"
                style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                disabled={createMutation.isPending || updateMutation.isPending}
                fullWidth
              >
                {createMutation.isPending || updateMutation.isPending ? 'Сохранение...' : 'Сохранить'}
              </Button>
              <Button variant="secondary" onClick={handleCancel} fullWidth>
                Отмена
              </Button>
            </div>

            {(createMutation.isError || updateMutation.isError) && (
              <p className="text-sm" style={{ color: 'var(--danger)' }}>
                {((createMutation.error || updateMutation.error) as Error)?.message || 'Ошибка сохранения'}
              </p>
            )}
          </div>
        ) : status === 'success' && config ? (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-hint)' }}>Базовая цена:</span>
              <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                {kopecksToRubles(config.base_price).toLocaleString('ru-RU')} ₽/мес
              </span>
            </div>

            <div className="flex justify-between">
              <span style={{ color: 'var(--text-hint)' }}>День списания:</span>
              <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                {config.billing_day} число
              </span>
            </div>

            {config.description && (
              <div>
                <span className="text-sm" style={{ color: 'var(--text-hint)' }}>
                  Описание:
                </span>
                <p className="text-sm mt-1" style={{ color: 'var(--text-primary)' }}>
                  {config.description}
                </p>
              </div>
            )}

            <div className="pt-3 mt-3" style={{ borderTop: '1px solid var(--separator)' }}>
              <Button
                variant="secondary"
                onClick={() => pushScreen('payment-overrides', { groupId, groupName })}
                fullWidth
              >
                Индивидуальные цены (льготы)
              </Button>
            </div>

            <div className="pt-2">
              <Button
                variant="secondary"
                onClick={() => {
                  if (confirm('Удалить настройки оплаты для этой группы?')) {
                    deleteMutation.mutate()
                  }
                }}
                disabled={deleteMutation.isPending}
                fullWidth
              >
                <span style={{ color: 'var(--danger)' }}>Удалить настройки</span>
              </Button>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  )
}
